import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryArticles } from '@/mocks/data';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || categories[0].slug;
  const [animateKey, setAnimateKey] = useState(0);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab) || categories[0],
    [activeTab]
  );

  const filteredArticles = useMemo(
    () => categoryArticles.filter((a) => a.category === activeTab),
    [activeTab]
  );

  useEffect(() => {
    setAnimateKey((prev) => prev + 1);
  }, [activeTab]);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const colorThemes: Record<string, { activeBg: string; activeText: string; iconBg: string; iconText: string }> = {
    primary: { activeBg: 'bg-primary-100', activeText: 'text-primary-700', iconBg: 'bg-primary-500', iconText: 'text-background-50' },
    accent: { activeBg: 'bg-accent-100', activeText: 'text-accent-700', iconBg: 'bg-accent-500', iconText: 'text-background-50' },
    secondary: { activeBg: 'bg-secondary-100', activeText: 'text-secondary-700', iconBg: 'bg-secondary-500', iconText: 'text-background-50' },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 太阳娱乐娱乐分类 | 太阳娱乐官网`}
        description={`太阳娱乐${currentCategory.name}频道为您提供${currentCategory.description}。涵盖电子竞技、娱乐资讯、体育赛事、精品游戏等多元娱乐内容。`}
        keywords={`太阳娱乐,${currentCategory.name},娱乐分类,太阳娱乐官网`}
      />
      <SchemaOrg type="WebPage" data={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${currentCategory.name} - 太阳娱乐`,
        url: `https://hznaweisi.cn/categories?tab=${activeTab}`,
        description: currentCategory.description,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb />

          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-2">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  娱乐分类
                </h1>
                <p className="text-foreground-600 mt-2">探索太阳娱乐多元化娱乐项目，发现您的兴趣所在</p>
              </div>
            </div>
          </section>

          <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => {
                  const theme = colorThemes[cat.color] || colorThemes.primary;
                  const isActive = activeTab === cat.slug;
                  return (
                    <button
                      key={cat.slug}
                      onClick={() => handleTabChange(cat.slug)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        isActive
                          ? `${theme.activeBg} ${theme.activeText}`
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        isActive ? `${theme.iconBg} ${theme.iconText}` : 'bg-background-200 text-foreground-500'
                      }`}>
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6" key={animateKey}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-xl text-primary-600`}></i>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground-950">{currentCategory.name}</h2>
                  <p className="text-sm text-foreground-600">{currentCategory.description}</p>
                </div>
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/articles/${article.slug}`}
                      className="group block bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative overflow-hidden aspect-[16/10]">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                          <span>{article.date}</span>
                          <span>·</span>
                          <span>{article.readTime}阅读</span>
                          <span>·</span>
                          <span>{article.views.toLocaleString()} 浏览</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无文章，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}